"use client";

import { useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button, SubmitButton } from "@/components/ui/button";
import { showToast } from "@/components/ui/sonner";
import { customInstance } from "@/lib/api/client";
import {
  isPermissionDenied,
  PAYMENT_METHOD_FORBIDDEN_MESSAGE,
} from "@/lib/permissions";
import {
  StripeElementsProvider,
  ELEMENT_STYLE,
} from "./stripe-elements-provider";

interface UpdatePaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpdatePaymentModal({
  open,
  onOpenChange,
}: UpdatePaymentModalProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} dismissible={false}>
      <ModalContent size="md">
        <ModalHeader title="Add payment method" iconName="IconCreditCard1" />
        <StripeElementsProvider>
          <AddPaymentForm onClose={() => onOpenChange(false)} />
        </StripeElementsProvider>
      </ModalContent>
    </Modal>
  );
}

function AddPaymentForm({ onClose }: { onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const [cardName, setCardName] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const canSave = !isBusy && !!stripe && cardComplete;

  const handleSave = async () => {
    if (!stripe || !elements) {
      showToast.error("Payment form not loaded.");
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      showToast.error("Card details not loaded.");
      return;
    }

    setIsBusy(true);
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumber,
        billing_details: cardName.trim()
          ? { name: cardName.trim() }
          : undefined,
      });

      if (error) {
        showToast.error("Card error", {
          description: error.message ?? "We couldn't validate your card.",
        });
        setIsBusy(false);
        return;
      }

      await customInstance("/api/billing/payment-methods/attach", {
        method: "POST",
        body: JSON.stringify({ payment_method_id: paymentMethod.id }),
        headers: { "Content-Type": "application/json" },
      });
      await queryClient.invalidateQueries({
        queryKey: ["billing", "payment-methods"],
      });
      showToast.success("Card added");
      onClose();
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : "";
      let description: string;
      if (isPermissionDenied(err)) {
        description = PAYMENT_METHOD_FORBIDDEN_MESSAGE;
      } else if (raw && !/internal server error/i.test(raw)) {
        description = raw;
      } else {
        description = "Something went wrong. Please try again.";
      }
      showToast.error("Couldn't add card", { description });
      setIsBusy(false);
    }
  };

  return (
    <>
      <ModalBody className="flex flex-col gap-3">
        {/* Full name on card */}
        <div className="flex flex-col gap-0.5">
          <Label>Name on card</Label>
          <Input
            type="text"
            variant="default"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            placeholder="Full name on card"
          />
        </div>

        {/* Card number */}
        <div className="flex flex-col gap-0.5">
          <Label>Card information</Label>
          <div className="flex h-9 items-center rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors focus-within:border-contrast/30">
            <CardNumberElement
              options={{
                style: ELEMENT_STYLE,
                showIcon: true,
                placeholder: "1234 1234 1234 1234",
              }}
              className="w-full"
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>
        </div>

        {/* Expiry + CVC */}
        <div className="flex gap-3">
          <div className="flex h-9 flex-1 items-center rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors focus-within:border-contrast/30">
            <CardExpiryElement
              options={{ style: ELEMENT_STYLE }}
              className="w-full"
            />
          </div>
          <div className="flex h-9 flex-1 items-center rounded-md border border-contrast/[0.15] bg-contrast/6 px-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors focus-within:border-contrast/30">
            <CardCvcElement
              options={{ style: ELEMENT_STYLE }}
              className="w-full"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          size="sm"
          onClick={onClose}
          disabled={isBusy}
        >
          Cancel
        </Button>
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          loading={isBusy}
          loadingLabel="Saving"
        >
          Add card
        </SubmitButton>
      </ModalFooter>
    </>
  );
}
